import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Checkbox,
  Divider,
  MenuItem,
  Popover,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { tokens } from '../../global/theme/tokens';
import ExoAvatar, { getInitials } from '../exo/ExoAvatar';

/**
 * Dropdown to pick several employees, for filter bars that would get too wide
 * with one avatar per employee.
 *
 * users: the employees to choose from
 * selectedIds: ids of the picked employees
 * onChange: receives the new array of ids
 * getInfo: optional text on the right of an entry, e.g. a task count
 * withUnassigned: adds the "not assigned" entry below the employees
 */
const EmployeesMultiSelect = ({
  users = [],
  selectedIds = [],
  onChange,
  getInfo,
  withUnassigned,
  unassignedSelected,
  onUnassignedChange,
  label,
  className = '',
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState(null);

  function toggleUser(userId) {
    onChange(
      selectedIds.includes(userId)
        ? selectedIds.filter((id) => id !== userId)
        : [...selectedIds, userId],
    );
  }

  const selectedUsers = users.filter((user) => selectedIds.includes(user.id));
  const selectedCount = selectedIds.length + (unassignedSelected ? 1 : 0);

  return (
    <Box className={className}>
      <Button
        onClick={(e) => setAnchorEl(e.currentTarget)}
        className="flex items-center gap-2"
        sx={{
          borderRadius: '8px',
          paddingY: '4px',
          color: 'inherit',
          textTransform: 'none',
          backgroundColor: colors.bgInput,
          ':hover': { backgroundColor: `${colors.info}20` },
        }}
      >
        <PersonIcon sx={{ fontSize: '16px', opacity: 0.6 }} />
        <Typography fontSize="12px" className="opacity-60">
          {label || t('Employee')}
        </Typography>
        {selectedCount ? (
          <AvatarGroup
            max={4}
            sx={{
              '& .MuiAvatar-root': {
                width: '22px',
                height: '22px',
                fontSize: '10px',
                borderColor: colors.glassSolid,
              },
            }}
          >
            {selectedUsers.map((user) => (
              <Avatar
                key={user.id}
                alt={`${user.firstName} ${user.lastName}`}
                src={user.picture?.thumbnailUrl}
                sx={{ backgroundColor: colors.glassSolid }}
              >
                {getInitials(`${user.firstName} ${user.lastName}`)}
              </Avatar>
            ))}

            {unassignedSelected && (
              <Avatar
                sx={{
                  backgroundColor: 'transparent',
                  border: `1px dashed ${colors.text}50 !important`,
                }}
              >
                <PersonOffIcon sx={{ fontSize: '13px', opacity: 0.6 }} />
              </Avatar>
            )}
          </AvatarGroup>
        ) : (
          <Typography fontSize="12px" fontWeight={500}>
            {t('All')}
          </Typography>
        )}
        <KeyboardArrowDownIcon
          sx={{
            fontSize: '18px',
            transform: `rotate(${anchorEl ? 180 : 0}deg)`,
            transition: 'transform 200ms',
          }}
        />
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        // the global backdrop blurs everything behind any modal
        sx={{
          '.MuiBackdrop-root': {
            bgcolor: 'transparent',
            background: 'unset',
            backdropFilter: 'unset',
          },
        }}
        slotProps={{
          paper: {
            sx: {
              minWidth: '290px',
              maxHeight: '360px',
              borderRadius: '12px',
              padding: '4px',
              backgroundColor: colors.glassSolid,
              backgroundImage: 'unset',
              border: `1px solid ${colors.text}15`,
              // the global list styles put a border on every item
              '& .MuiMenuItem-root': {
                border: 'none !important',
                borderRadius: '8px',
                gap: 1,
                paddingY: '4px',
              },
            },
          },
        }}
      >
        {users.map((user) => {
          const name = `${user.firstName} ${user.lastName}`;
          const info = getInfo ? getInfo(user) : null;
          return (
            <MenuItem key={user.id} onClick={() => toggleUser(user.id)}>
              <Checkbox
                size="small"
                checked={selectedIds.includes(user.id)}
                sx={{ padding: '4px' }}
              />
              <ExoAvatar
                name={name}
                picture={user.picture?.thumbnailUrl}
                size="26px"
              />
              <Box className="flex flex-col grow min-w-0">
                <Typography fontSize="13px" fontWeight={500}>
                  {name}
                </Typography>
                {user.position && (
                  <Typography fontSize="11px" className="opacity-60">
                    {user.position}
                  </Typography>
                )}
              </Box>
              {info && (
                <Typography
                  fontSize="11px"
                  className="monospace opacity-60 whitespace-nowrap"
                >
                  {info}
                </Typography>
              )}
            </MenuItem>
          );
        })}

        {withUnassigned && [
          <Divider key="unassigned-divider" sx={{ marginY: '4px' }} />,
          <MenuItem
            key="unassigned"
            onClick={() => onUnassignedChange(!unassignedSelected)}
          >
            <Checkbox
              size="small"
              checked={Boolean(unassignedSelected)}
              sx={{ padding: '4px' }}
            />
            <ExoAvatar
              size="26px"
              bgcolor="transparent"
              icon={<PersonOffIcon sx={{ fontSize: '15px', opacity: 0.6 }} />}
            />
            <Typography fontSize="13px" fontWeight={500}>
              {t('Not assigned')}
            </Typography>
          </MenuItem>,
        ]}
      </Popover>
    </Box>
  );
};

export default EmployeesMultiSelect;
